import { z } from "zod"

export enum PvWattsArrayType {
  "Fixed - Open Rack" = `0`,
  "Fixed - Roof Mounted" = `1`,
  "1-Axis" = `2`,
  "1-Axis Backtracking" = `3`,
  "2-Axis" = `4`,
}

export enum PvWattsModuleType {
  "Standard" = `0`,
  "Premium" = `1`,
  "Thin film" = `2`,
}

export const datasetSchema = z.enum([`nsrdb`, `tmy2`, `tmy3`, `intl`])

export const useWfAlbedoSchema = z.enum([`0`, `1`]).optional()

export const pvWattsV8InputsSchema = z.object({
  /** The address to use. Required if lat/lon or file_id not specified. */
  address: z.string().optional().nullable(),

  /**
   * Ground reflectance.
   * A value of 0 means completely non-reflective,
   * and 1 means completely reflective.
   * Specify either a pipe-delimited array of
   * 12 monthly values or a single value for all months.
   */
  albedo: z.number().min(0).max(1).optional(),

  /**
   * Array type.
   * Options: 0, 1, 2, 3, 4
   * 0: Fixed - Open Rack, 1: Fixed - Roof Mounted,
   * 2: 1-Axis, 3: 1-Axis Backtracking, 4: 2-Axis
   */
  array_type: z.nativeEnum(PvWattsArrayType),

  /**
   * Azimuth angle (degrees).
   * Min: 0, Max: < 360
   */
  azimuth: z.coerce.number().min(0).max(360),

  /**
   * The ratio of rear-side efficiency to front-side efficiency.
   * Range: 0 - 1
   */
  bifaciality: z.number().min(0).max(1).optional(),

  /**
   * Return the data using JSONP and the given callback function
   * (only applicable when using the json format).
   */
  callback: z.string().optional(),

  /**
   * DC to AC ratio.
   * Range: must be positive
   */
  dc_ac_ratio: z.preprocess(
    (value) => (value === null ? undefined : value),
    z.coerce.number().positive().optional().default(1.2),
  ),

  /**
   * Ground coverage ratio.
   * Range: 0.01 - 0.99
   */
  gcr: z.number().min(0.01).max(0.99).default(0.4).optional(),

  /**
   * Inverter efficiency at rated power.
   * Range: 90 - 99.5
   */
  inv_eff: z.preprocess(
    (value) => (value === null ? undefined : value),
    z.coerce.number().min(90).max(99.5).optional().default(96),
  ),

  /**
   * The latitude for the location to use.
   * Required if address or file_id not specified.
   * Range: -90 to 90
   */
  lat: z.coerce.number().min(-90).max(90),

  /**
   * The longitude for the location to use.
   * Required if address or file_id not specified.
   * Range: -180 to 180
   */
  lon: z.coerce.number().min(-180).max(180),

  /**
   * System losses (percent).
   * Range: -5 to 99
   */
  losses: z.coerce.number().min(-5).max(99),

  /**
   * Module type.
   * Options: 0, 1, 2
   * 0: Standard, 1: Premium, 2: Thin film
   */
  module_type: z.nativeEnum(PvWattsModuleType),

  /**
   * The search radius to use when searching
   * for the closest climate data station (miles).
   * Pass in radius=0 to use the closest station regardless of the distance.
   */
  radius: z.number().int().default(100).optional(),

  /**
   * Reduction in incident solar irradiance caused by dust
   * or other seasonal soiling of the module surface.
   * Specify a pipe-delimited array of 12 monthly values.
   * Range: 0 - 100
   */
  // soiling: z.array(z.number().min(0).max(100)).length(12).optional(),

  /**
   * Nameplate capacity (kW).
   */
  system_capacity: z.coerce.number().min(0.05).max(500000),

  /**
   * Tilt angle (degrees).
   * Range: 0 to 90
   */
  tilt: z.coerce.number().min(0).max(90),

  /**
   * Granularity of the output response.
   * Options: monthly, hourly
   */
  timeframe: z.string().default(`monthly`).optional(),

  /**
   * Use hourly or subhourly albedo data from the weather
   * file instead of the monthly albedo values, if available.
   * - 0: Do not use weather file albedo values,
   * - 1: Use weather file albedo values.
   */
  use_wf_albedo: useWfAlbedoSchema.optional(),
})

export type PvWattsV8Inputs = z.infer<typeof pvWattsV8InputsSchema>
